/**
 * reglagesDeTexte — le SEUL endroit qui traduit un réglage de texte demandé par
 * l'agent (`format_text`, unités d'avocat) en ce que l'éditeur reçoit, et qui
 * dit ce qu'« être conforme » veut dire pour ce réglage (#1864, spec #1858,
 * règles communes n°3 et n°4).
 *
 * Module pur : il ne connaît ni l'éditeur ni le navigateur. L'exécuteur
 * (`formatTextExecutor.ts`) lui demande quoi écrire, la vérification
 * (`verificationDeForme.ts`) lui demande comment juger.
 *
 * ## Ce qui est mesuré, et dont ces règles dépendent
 *
 * Sonde `tests/ws-mocked/mesure-format-text-police-taille-couleur.spec.ts`
 * (SuperDoc 2.15.1, 2026-10-02, plans atomiques directs, éditeur en mode
 * suggestion ; la lettre et le numéro renvoient au geste de la sonde) :
 *
 * - **Couleur** (C1 à C9). `color` accepte `#RRGGBB` et `auto` ; le fichier
 *   porte `w:color w:val="C00000"` ou `"auto"`. L'éditeur IGNORE la casse :
 *   la même couleur redemandée en minuscules est sans effet. `null` efface la
 *   couleur posée à la main.
 * - **Police** (P1 à P6). `fontFamily` écrit `w:rFonts` (`ascii` et `hAnsi`).
 *   L'éditeur RESPECTE la casse du nom : `times new roman` demandé sur un texte
 *   en `Times New Roman` est réécrit, pas refusé. `null` efface la police posée
 *   à la main.
 * - **Taille** (T1 à T5). `fontSize` est en points (`15` → `w:sz="30"`,
 *   `12.5` → `w:sz="25"`). `null` efface la taille posée à la main.
 * - **Surlignage** (S1 à S7, R4, R5, R12 à R14). `'none'` EFFACE la marque
 *   posée à la main ; il n'écrit pas `w:highlight w:val="none"`. Sur un
 *   surlignage porté par un STYLE NOMMÉ, `'none'` et `null` sont refusés
 *   (`NO_OP`), dans un plan comme hors plan, et un ombrage ne le couvre pas :
 *   l'éditeur n'a aucune écriture qui le retire. `'white'` est accepté, écrit
 *   `w:highlight w:val="white"` et peint un fond blanc.
 * - **Gras, italique, souligné** (R1 à R3). `false` écrit la négation à la main
 *   (`w:b w:val="0"`, `w:u w:val="none"`), même contre un style.
 * - **Emplacements non lus** (E1 à E4). Effacer la police (`null`) retire tout
 *   `w:rFonts`, même réduit à `w:cs` : sa seule présence vaut « posé à la
 *   main ». Effacer la taille ne touche que `w:sz` : sur un `w:szCs` seul,
 *   c'est un `NO_OP`. D'où `aLaMain`, que le lecteur déduit de ces deux éléments.
 * - **Sans effet** (C2, C6, P5, T5, S3, S7, R7, R9 à R11, X2 à X4). Une étape
 *   dont AUCUN réglage ne change rien fait refuser le plan entier (`NO_OP`,
 *   jeté) : même valeur déjà posée à la main, ou effacement (`null`, `'none'`
 *   de surlignage) là où rien n'est posé à la main. Une étape dont UN réglage
 *   au moins change quelque chose est acceptée.
 *
 * ## « Retirer », réglage par réglage
 *
 * Le critère du ticket est l'écran : après « retirer », le texte ne montre plus
 * la forme, même quand elle venait d'un style nommé. Mais retirer n'ajoute de
 * forme directe que là où un style l'impose : partout ailleurs, la forme posée
 * à la main est simplement effacée. La décision se prend donc sur ce que le
 * lecteur de forme dit de chaque passage : ce que la forme directe pose
 * (`aLaMain`, connu même quand la valeur ne se lit pas) et ce que la cascade
 * donnerait sans elle (`sousLaMain`).
 *
 * | réglage | ce qui est écrit | conforme quand |
 * |---|---|---|
 * | gras, italique, souligné (`false`) | la négation, à la main | la valeur lue est `false` |
 * | surlignage (`none`) | la marque effacée ; un surlignage BLANC si un style surligne dessous | lu `none`, ou `white` (dit dans le reçu) |
 * | couleur (`none`) | la couleur effacée ; la couleur automatique, à la main, si un style colore dessous | lu `auto` ou noir |
 * | police, taille (`none`) | la forme directe effacée | plus rien n'est posé à la main |
 *
 * Une police et une taille ne peuvent pas être « absentes » : les retirer rend
 * celles du style du paragraphe, que le reçu nomme. Le noir (`#000000`) est
 * tenu pour la couleur d'un texte sans couleur : le gabarit Hector l'écrit dans
 * chacun de ses styles, et l'écran ne le distingue pas de la couleur
 * automatique.
 *
 * Limite connue : la couleur automatique est écrite sur tout un paragraphe dès
 * que, pour UN de ses passages, ce que donne le style ne se lit pas (couleur de
 * thème, style absent du fichier).
 *
 * ⚠️ Le surlignage blanc est un pis-aller imposé par l'éditeur : le fichier
 * porte un surlignage blanc là où Word aurait écrit « aucun ». Invisible sur
 * une page blanche, il se verrait sur un fond coloré.
 */

import { CANVAS_FORMAT_ANY, CANVAS_FORMAT_REMOVE, type CanvasFormatOrigin, type CanvasFormatSettingValue, type CanvasTextFormatSettingKey } from '@hector/shared-types';
import type { FormeDePassage, Reglage } from '@hector/docx-forme';

/** Ce que le lecteur de forme dit d'un passage, pour UN réglage. */
export interface PassageLu {
  /** Le réglage tel que l'écran le montre : sa valeur et son origine, ou la raison pour laquelle il ne se lit pas. */
  lu: Reglage<CanvasFormatSettingValue>;
  /** Vrai si la forme directe du passage pose ce réglage, que sa valeur se lise ou non. */
  aLaMain: boolean;
  /** Ce que la cascade donnerait sans la forme directe. Rendu pour le surlignage et la couleur seulement. */
  sousLaMain?: Reglage<string>;
  /**
   * Le réglage tel que le FICHIER le porte, quand l'écran le peint autrement
   * (taille d'un exposant, souligné d'un passage déplacé) : `lu` est alors non
   * lisible, mais c'est au fichier que l'éditeur compare une écriture.
   */
  auFichier?: Reglage<CanvasFormatSettingValue>;
}

/**
 * Ce que la relecture dit d'un passage pour un réglage demandé. `reste` : la
 * demande est satisfaite, et voici ce que le texte porte encore (la police du
 * style après un retrait, le surlignage blanc posé contre celui d'un style).
 */
export type VerdictDeReglage =
  | { issue: 'conforme'; reste?: CeQuiReste }
  | { issue: 'different'; lu: CanvasFormatSettingValue }
  | { issue: 'non_lisible' };

/**
 * Ce que le texte porte encore après un retrait conforme. Sans `valeur` : elle
 * ne se lit pas, et l'origine dit seulement « pas de la main » (`inherited`).
 */
export type CeQuiReste =
  | { valeur: CanvasFormatSettingValue; origine: CanvasFormatOrigin }
  | { origine: 'inherited' };

/** Ce que l'éditeur reçoit pour UN réglage, et si l'écrire sur ces passages ne changerait rien. */
export interface EcritureDeReglage {
  /** Les clés et valeurs de l'`inline` d'une étape `format.apply`. `null` efface la forme directe. */
  inline: Record<string, boolean | string | number | null>;
  /** Vrai si chaque passage est déjà dans l'état que cette écriture produirait : l'éditeur la refuserait (`NO_OP`). */
  sansEffet: boolean;
}

type ValeurEcrite = boolean | string | number | null;

/** Comment un réglage se RETIRE : ce qui part à l'éditeur, et ce qui satisfait la demande. */
interface RetraitDeReglage {
  ecrire: (passages: readonly PassageLu[]) => { valeur: ValeurEcrite; sansEffet: boolean };
  juger: (passage: PassageLu) => VerdictDeReglage;
}

interface RegleDeReglage {
  /** La clé du réglage dans l'`inline` de l'éditeur. */
  cle: string;
  /** Absent : le réglage est un booléen, il se retire par la valeur `false`. */
  retrait?: RetraitDeReglage;
}

/** La couleur automatique de Word : celle d'un texte sans couleur, et la valeur du lecteur pour elle. */
const COULEUR_AUTOMATIQUE = 'auto';
/** Ce que l'écran montre comme un texte sans couleur : la couleur automatique, ou le noir. */
const SANS_COULEUR: ReadonlySet<CanvasFormatSettingValue> = new Set([COULEUR_AUTOMATIQUE, '#000000']);
/** Le surlignage qui couvre celui d'un style nommé, faute d'écriture qui le retire. */
const SURLIGNAGE_BLANC = 'white';
const SANS_SURLIGNAGE = 'none';

const poseALaMain = ({ lu, auFichier = lu }: PassageLu, valeur: ValeurEcrite): boolean => (
  auFichier.lisible && auFichier.origine === 'direct' && auFichier.valeur === valeur
);
const rienALaMain = (passages: readonly PassageLu[]): boolean => passages.every((passage) => !passage.aLaMain);

/** Effacer la forme directe : sans effet là où rien n'est posé à la main, lisible ou non. */
const effacer = (valeur: ValeurEcrite = null): RetraitDeReglage['ecrire'] => (passages) => ({ valeur, sansEffet: rienALaMain(passages) });
/** Écrire à la main une valeur qui couvre celle du style. */
const couvrir = (valeur: string, passages: readonly PassageLu[]) => ({ valeur, sansEffet: passages.every((passage) => poseALaMain(passage, valeur)) });

/** Police et taille : retirer rend ce que donne le style ; conforme dès que plus rien n'est posé à la main. */
const RETOUR_AU_STYLE: RetraitDeReglage = {
  ecrire: effacer(),
  juger: ({ lu, aLaMain }) => {
    // Une forme directe reste. Sa valeur n'est « ce qui diffère » que si c'est elle qui se lit : posée aux
    // seuls emplacements que le lecteur ne lit pas (`w:rFonts w:cs`), c'est la police du style qui se lit.
    if (aLaMain) return lu.lisible && lu.origine === 'direct' ? { issue: 'different', lu: lu.valeur } : { issue: 'non_lisible' };
    return { issue: 'conforme', reste: lu.lisible ? { valeur: lu.valeur, origine: lu.origine } : { origine: 'inherited' } };
  },
};

const REGLES: Readonly<Record<CanvasTextFormatSettingKey, RegleDeReglage>> = {
  bold: { cle: 'bold' },
  italic: { cle: 'italic' },
  underline: { cle: 'underline' },
  highlight: {
    cle: 'highlight',
    retrait: {
      // Un style qui surligne dessous : effacer la marque ne changerait rien, ou
      // ferait reparaître le surlignage du style. Sinon la marque est effacée,
      // un blanc posé à la main compris.
      ecrire: (passages) => (
        passages.some(({ sousLaMain }) => sousLaMain?.lisible === true && sousLaMain.valeur !== SANS_SURLIGNAGE)
          ? couvrir(SURLIGNAGE_BLANC, passages)
          : effacer(SANS_SURLIGNAGE)(passages)
      ),
      juger: ({ lu }) => {
        if (!lu.lisible) return { issue: 'non_lisible' };
        if (lu.valeur === SANS_SURLIGNAGE) return { issue: 'conforme' };
        return lu.valeur === SURLIGNAGE_BLANC
          ? { issue: 'conforme', reste: { valeur: lu.valeur, origine: lu.origine } }
          : { issue: 'different', lu: lu.valeur };
      },
    },
  },
  color: {
    cle: 'color',
    retrait: {
      // La couleur automatique n'est écrite à la main que là où le style colore
      // — ou là où ce qu'il donne ne se lit pas : rien ne prouve alors qu'il ne
      // colore pas. Ailleurs, la couleur posée à la main est effacée.
      ecrire: (passages) => (
        passages.some(({ sousLaMain }) => sousLaMain !== undefined && !(sousLaMain.lisible && SANS_COULEUR.has(sousLaMain.valeur)))
          ? couvrir(COULEUR_AUTOMATIQUE, passages)
          : effacer()(passages)
      ),
      juger: ({ lu }) => {
        if (!lu.lisible) return { issue: 'non_lisible' };
        if (!SANS_COULEUR.has(lu.valeur)) return { issue: 'different', lu: lu.valeur };
        // Le noir qu'un style ou la main nomme reste dit ; la couleur automatique n'est pas « quelque chose qui reste ».
        return lu.valeur === COULEUR_AUTOMATIQUE ? { issue: 'conforme' } : { issue: 'conforme', reste: { valeur: lu.valeur, origine: lu.origine } };
      },
    },
  },
  fontFamily: { cle: 'fontFamily', retrait: RETOUR_AU_STYLE },
  fontSizePt: { cle: 'fontSize', retrait: RETOUR_AU_STYLE },
};

/** Le retrait que demande `demande` pour ce réglage, ou `undefined` si c'est une valeur à poser. */
function retraitDemande(setting: CanvasTextFormatSettingKey, demande: CanvasFormatSettingValue): RetraitDeReglage | undefined {
  return demande === CANVAS_FORMAT_REMOVE ? REGLES[setting].retrait : undefined;
}

/**
 * Met la valeur demandée sous la forme que rend le lecteur de forme, pour que
 * demande et relecture se comparent : une couleur passe en capitales, et le mot
 * qui retire est reconnu quelle que soit sa casse — `fontFamily:"None"` est un
 * retrait, pas une police nommée « None ».
 */
export function normaliserLaDemande(setting: CanvasTextFormatSettingKey, valeur: CanvasFormatSettingValue): CanvasFormatSettingValue {
  if (typeof valeur !== 'string') return valeur;
  if (REGLES[setting].retrait !== undefined && valeur.toLowerCase() === CANVAS_FORMAT_REMOVE) return CANVAS_FORMAT_REMOVE;
  return setting === 'color' ? valeur.toUpperCase() : valeur;
}

/** Ce que le lecteur de forme dit d'un passage pour `setting`, sous la forme que ces règles jugent. */
export function passageLuPour(passage: FormeDePassage, setting: CanvasTextFormatSettingKey): PassageLu {
  return {
    lu: passage[setting],
    aLaMain: passage.aLaMain.includes(setting),
    ...(setting === 'highlight' || setting === 'color' ? { sousLaMain: passage.sousLaMain[setting] } : {}),
    ...((setting === 'fontSizePt' || setting === 'underline') && passage.auFichier !== undefined ? { auFichier: passage.auFichier[setting] } : {}),
  };
}

/**
 * Juge ce que le fichier relu porte, pour un passage, au regard de la demande.
 *
 * @param demande La valeur demandée, normalisée ({@link normaliserLaDemande}).
 */
export function jugerLeReglage(setting: CanvasTextFormatSettingKey, demande: CanvasFormatSettingValue, passage: PassageLu): VerdictDeReglage {
  const retrait = retraitDemande(setting, demande);
  if (retrait !== undefined) return retrait.juger(passage);
  const { lu } = passage;
  if (!lu.lisible) return { issue: 'non_lisible' };
  return lu.valeur === demande ? { issue: 'conforme' } : { issue: 'different', lu: lu.valeur };
}

/**
 * Ce que le lecteur permet de dire d'un passage face à UN réglage d'un filtre
 * `onlyWhere`. `non_evaluable` : le réglage ne se lit pas sur ce passage — il
 * ne satisfait pas le filtre, et rien ne prouve qu'il ne l'aurait pas satisfait.
 */
export type VerdictDeFiltre = 'satisfait' | 'non_satisfait' | 'non_evaluable';

/**
 * Confronte un passage lu à la valeur qu'un filtre `onlyWhere` attend pour un
 * réglage (#1865). C'est ce que l'ÉCRAN montre qui décide, d'où que vienne la
 * forme : un gras de style satisfait `bold:true`, un gras de style remis en
 * maigre à la main ne le satisfait pas.
 *
 * Pour le surlignage et la couleur, « n'en a pas » (`none`) est exactement ce
 * qu'un retrait conforme laisse — le blanc posé contre le surlignage d'un
 * style, le noir d'un style — et « en a un » (`any`) son contraire : le filtre
 * et le retrait partagent la même définition de l'absence. Le noir (`#000000`)
 * filtre donc comme `none`.
 *
 * Les textes se comparent sans égard à la casse : l'avocat dit « arial », le
 * fichier « Arial ».
 *
 * @param attendu La valeur du filtre, telle que le contrat partagé l'admet.
 */
export function filtrerLeReglage(setting: CanvasTextFormatSettingKey, attendu: CanvasFormatSettingValue, passage: PassageLu): VerdictDeFiltre {
  const quelconque = attendu === CANVAS_FORMAT_ANY;
  const absence = setting === 'highlight' || setting === 'color' ? REGLES[setting].retrait : undefined;
  // Le noir est la couleur d'un texte sans couleur : le demander, c'est demander « sans couleur ».
  const sans = attendu === CANVAS_FORMAT_REMOVE || (setting === 'color' && typeof attendu === 'string' && SANS_COULEUR.has(attendu.toUpperCase()));
  if (absence !== undefined && (quelconque || sans)) {
    const { issue } = absence.juger(passage);
    if (issue === 'non_lisible') return 'non_evaluable';
    return (issue === 'conforme') === quelconque ? 'non_satisfait' : 'satisfait';
  }
  const { lu } = passage;
  if (!lu.lisible) return 'non_evaluable';
  const egal = typeof attendu === 'string' && typeof lu.valeur === 'string'
    ? attendu.toLowerCase() === lu.valeur.toLowerCase()
    : attendu === lu.valeur;
  return egal ? 'satisfait' : 'non_satisfait';
}

/**
 * Ce que l'éditeur doit recevoir pour poser `demande` sur des passages lus, et
 * si cette écriture y changerait quelque chose.
 *
 * @param demande La valeur demandée, normalisée ({@link normaliserLaDemande}).
 * @param passages Ce que le lecteur dit de chaque passage de l'étendue visée, AVANT l'écriture.
 */
export function ecritureDuReglage(
  setting: CanvasTextFormatSettingKey,
  demande: CanvasFormatSettingValue,
  passages: readonly PassageLu[],
): EcritureDeReglage {
  const { valeur, sansEffet } = retraitDemande(setting, demande)?.ecrire(passages)
    ?? { valeur: demande, sansEffet: passages.every((passage) => poseALaMain(passage, demande)) };
  return { inline: { [REGLES[setting].cle]: valeur }, sansEffet };
}
